// Admin drill-in to a single driver's damage fund account.
//   - Balance hero + full ledger (reuses DamageFundLedger)
//   - Action bar: Add Damage Charge / NC Credit / Cash Out / Adjustment
//   - Reverse any entry (append-only undo)
//   - Account settings: map to driver login, biweekly rate, cap, archive
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatMoney } from '../utils/money';
import DamageFundLedger from './DamageFundLedger';
import ModalRoot from '../components/ModalRoot';

const TODAY = () => new Date().toISOString().slice(0, 10);

export default function DamageFundDriverPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, can } = useAuth();
  const canManage = isSuperAdmin || can('manage_damage_fund');

  const [account, setAccount] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // 'tx' | 'settings'
  const [txType, setTxType] = useState('damage_charge');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { account } = await api.getDamageFundAccount(id);
      const { transactions } = await api.getDamageFundTransactions(id);
      setAccount(account);
      setTxns(transactions);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function handleReverse(t) {
    if (!window.confirm(`Reverse this ${t.type} of ${formatMoney(Math.abs(t.amount))}? This posts an offsetting entry — the original line stays in the history.`)) return;
    try { await api.reverseDamageFundTransaction(t.id); await load(); }
    catch (e) { alert(e.message); }
  }

  async function handleExport() {
    const blob = await api.exportDamageFund({ account_id: id });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `damage-fund-${account.display_name}.xlsx`; a.click();
    window.URL.revokeObjectURL(url);
  }

  function openTx(type) { setTxType(type); setModal('tx'); }

  if (loading) return <div className="df-page">Loading…</div>;
  if (err) return <div className="df-page" style={{ color: '#ef4444' }}>{err}</div>;
  if (!account) return <div className="df-page">Account not found.</div>;

  return (
    <div className="df-page">
      <button type="button" className="btn btn-muted btn-sm" onClick={() => navigate('/damage-fund')} style={{ marginBottom: 16 }}>← All Drivers</button>

      {canManage && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => openTx('damage_charge')}>+ Damage Charge</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => openTx('nc_credit')}>+ NC Credit</button>
          <button type="button" className="btn btn-gold btn-sm" onClick={() => openTx('cash_out')}>+ Cash Out</button>
          <button type="button" className="btn btn-muted btn-sm" onClick={() => openTx('adjustment')}>+ Adjustment</button>
          <button type="button" className="btn btn-muted btn-sm" onClick={() => setModal('settings')} style={{ marginLeft: 'auto' }}>⚙ Settings</button>
        </div>
      )}

      <DamageFundLedger
        account={account}
        transactions={txns}
        canManage={canManage}
        onReverse={handleReverse}
        onExport={handleExport}
      />

      {modal === 'tx' && (
        <TransactionModal
          accountId={account.id}
          initialType={txType}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); }}
        />
      )}
      {modal === 'settings' && (
        <SettingsModal
          account={account}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); }}
        />
      )}
    </div>
  );
}

// ---- Add transaction modal -----------------------------------
function TransactionModal({ accountId, initialType, onClose, onSaved }) {
  const [type, setType] = useState(initialType);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [shop, setShop] = useState('');
  const [occurredOn, setOccurredOn] = useState(TODAY());
  const [explicitSign, setExplicitSign] = useState(-1); // for adjustment
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const TYPES = [
    { key: 'damage_charge', label: 'Damage Charge', hint: 'Reduces the driver\u2019s balance.' },
    { key: 'nc_credit', label: 'No-Charge Credit', hint: 'Adds to the balance (driver not at fault). Usually $30.' },
    { key: 'cash_out', label: 'Cash Out', hint: 'Paid out to the driver. Reduces the balance.' },
    { key: 'adjustment', label: 'Adjustment', hint: 'Manual correction — pick credit (+) or debit (\u2212).' },
  ];
  const current = TYPES.find(t => t.key === type);

  async function save() {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a positive amount.'); return; }
    setSaving(true); setErr('');
    try {
      await api.addDamageFundTransaction(accountId, {
        type, amount: amt, description: description || null, shop: shop || null,
        occurred_on: occurredOn, explicitSign: type === 'adjustment' ? explicitSign : undefined,
      });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content df-modal" onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Add Transaction</h2>

        <label className="form-label">Type</label>
        <select className="form-input" value={type} onChange={e => setType(e.target.value)}>
          {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <div style={{ fontSize: 12, color: '#64748b', margin: '4px 0 12px' }}>{current?.hint}</div>

        {type === 'adjustment' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button type="button" className={`btn btn-sm ${explicitSign === 1 ? 'btn-primary' : 'btn-muted'}`} onClick={() => setExplicitSign(1)}>Credit (+)</button>
            <button type="button" className={`btn btn-sm ${explicitSign === -1 ? 'btn-danger' : 'btn-muted'}`} onClick={() => setExplicitSign(-1)}>Debit (−)</button>
          </div>
        )}

        <label className="form-label">Amount ($)</label>
        <input className="form-input" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} autoFocus />

        <label className="form-label" style={{ marginTop: 12 }}>Date</label>
        <input className="form-input" type="date" value={occurredOn} onChange={e => setOccurredOn(e.target.value)} />

        <label className="form-label" style={{ marginTop: 12 }}>Description</label>
        <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder={type === 'damage_charge' ? 'e.g. Mini Cooper, Oxford at Lake Worth' : ''} />

        {type === 'damage_charge' && (
          <>
            <label className="form-label" style={{ marginTop: 12 }}>Shop (optional)</label>
            <input className="form-input" value={shop} onChange={e => setShop(e.target.value)} placeholder="e.g. El Padrino" />
          </>
        )}

        {err && <div style={{ color: '#ef4444', marginTop: 12, fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" className="btn btn-muted" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Post Transaction'}</button>
        </div>
      </div>
    </ModalRoot>
  );
}

// ---- Account settings modal ----------------------------------
function SettingsModal({ account, onClose, onSaved }) {
  const [displayName, setDisplayName] = useState(account.display_name);
  const [regionGroup, setRegionGroup] = useState(account.region_group || '');
  const [rate, setRate] = useState(account.biweekly_rate);
  const [cap, setCap] = useState(account.cap_threshold);
  const [userId, setUserId] = useState(account.user_id || '');
  const [status, setStatus] = useState(account.status);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.getDamageFundMappableUsers().then(({ users }) => {
      // include currently-mapped user so the select can show them
      setUsers(users);
    }).catch(() => {});
  }, []);

  async function save() {
    setSaving(true); setErr('');
    try {
      await api.updateDamageFundAccount(account.id, {
        display_name: displayName,
        region_group: regionGroup || null,
        biweekly_rate: Number(rate),
        cap_threshold: Number(cap),
        user_id: userId ? Number(userId) : null,
        status,
      });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content df-modal" onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Account Settings</h2>

        <label className="form-label">Display Name</label>
        <input className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)} />

        <label className="form-label" style={{ marginTop: 12 }}>Group</label>
        <input className="form-input" value={regionGroup} onChange={e => setRegionGroup(e.target.value)} placeholder="e.g. Dallas, Fort Worth/Denton, Day Drivers" />

        <label className="form-label" style={{ marginTop: 12 }}>Mapped Driver Login</label>
        <select className="form-input" value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">— Unmapped —</option>
          {account.user_id && <option value={account.user_id}>{account.user_full_name} (current)</option>}
          {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
        </select>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Maps this fund to a dispatch login so the driver sees their own balance.</div>

        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Biweekly Rate ($)</label>
            <input className="form-input" type="number" min="0" step="0.01" value={rate} onChange={e => setRate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label">Cap Threshold ($)</label>
            <input className="form-input" type="number" min="0" step="0.01" value={cap} onChange={e => setCap(e.target.value)} />
          </div>
        </div>

        <label className="form-label" style={{ marginTop: 12 }}>Status</label>
        <select className="form-input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="archived">Archived (terminated driver)</option>
        </select>

        {err && <div style={{ color: '#ef4444', marginTop: 12, fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" className="btn btn-muted" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </ModalRoot>
  );
}
